import { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import CartContext from '../../context/CartContext';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { addToCart } = useContext(CartContext);
  
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`/api/products/${id}`);
        setProduct(res.data);
        
        // Fetch related products in the same category
        const relatedRes = await axios.get(`/api/products/category/${res.data.category}`);
        // Filter out the current product and limit to 3 related products
        const filtered = relatedRes.data
          .filter(item => item._id !== id)
          .slice(0, 3);
        
        setRelatedProducts(filtered);
        setLoading(false);
      } catch (err) {
        setError('Failed to load product');
        setLoading(false);
        console.error(err);
      }
    };
    
    fetchProduct();
    // Reset quantity when product changes
    setQuantity(1);
  }, [id]);
  
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0 && value <= (product?.stock || 1)) {
      setQuantity(value);
    }
  };
  
  const incrementQuantity = () => {
    if (quantity < (product?.stock || 1)) {
      setQuantity(quantity + 1);
    }
  };
  
  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  
  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      // Show toast or notification
      alert(`${quantity} ${quantity === 1 ? 'item' : 'items'} added to cart!`);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="neumorph p-8 rounded-full">
          <svg className="animate-spin h-8 w-8 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }
  
  if (error || !product) {
    return (
      <div className="text-center py-8">
        <div className="neumorph p-6 rounded-lg inline-block">
          <h3 className="text-xl text-red-600 mb-2">Error</h3>
          <p className="text-gray-700">{error || 'Product not found'}</p>
          <Link to="/products" className="neumorph-btn mt-4 px-4 py-2 inline-block">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-6">
        <ol className="flex text-gray-600 text-sm">
          <li>
            <Link to="/" className="hover:text-gray-900">Home</Link>
          </li>
          <li className="mx-2">/</li>
          <li>
            <Link to="/products" className="hover:text-gray-900">Products</Link>
          </li>
          <li className="mx-2">/</li>
          <li>
            <Link to={`/products?category=${product.category}`} className="hover:text-gray-900">
              {product.category}
            </Link>
          </li>
          <li className="mx-2">/</li>
          <li className="text-gray-900 font-medium">{product.name}</li>
        </ol>
      </nav>
      
      {/* Product Details */}
      <div className="neumorph p-6 rounded-lg mb-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Product Image */}
          <div className="md:w-1/2">
            <div className="neumorph p-4 rounded-lg">
              <img 
                src={product.imageUrl} 
                alt={product.name} 
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
          
          {/* Product Info */}
          <div className="md:w-1/2">
            <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
            
            <div className="mb-4">
              <span className="text-2xl font-semibold">${product.price.toFixed(2)}</span>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700">{product.description}</p>
            </div>
            
            <div className="mb-4">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                product.stock > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {product.stock > 0 ? `In Stock (${product.stock})` : 'Out of Stock'}
              </span>
            </div>
            
            {product.stock > 0 && (
              <div className="mb-6">
                <label htmlFor="quantity" className="block text-gray-700 mb-2">Quantity</label>
                <div className="flex">
                  <button 
                    onClick={decrementQuantity}
                    className="neumorph-btn px-3 py-2"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    id="quantity"
                    value={quantity}
                    onChange={handleQuantityChange}
                    className="neumorph-inset w-16 mx-2 px-3 py-2 text-center rounded-lg focus:outline-none"
                    min="1"
                    max={product.stock}
                  />
                  <button 
                    onClick={incrementQuantity}
                    className="neumorph-btn px-3 py-2"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex space-x-4">
              <button 
                onClick={handleAddToCart}
                className="neumorph-btn px-6 py-3 w-full"
                disabled={product.stock <= 0}
              >
                {product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
              </button>
            </div>
            
            {/* Additional Info */}
            <div className="mt-8">
              <div className="neumorph-inset p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Product Details</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li><span className="font-medium">Category:</span> {product.category}</li>
                  <li><span className="font-medium">SKU:</span> {product._id.substring(0, 8)}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Related Products</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedProducts.map(relatedProduct => (
              <div key={relatedProduct._id} className="neumorph rounded-lg overflow-hidden">
                <Link to={`/products/${relatedProduct._id}`}>
                  <img 
                    src={relatedProduct.imageUrl} 
                    alt={relatedProduct.name} 
                    className="w-full h-48 object-cover"
                  />
                </Link>
                <div className="p-4">
                  <Link to={`/products/${relatedProduct._id}`}>
                    <h3 className="text-lg font-semibold mb-2">{relatedProduct.name}</h3>
                  </Link>
                  <p className="text-gray-600 mb-4">${relatedProduct.price.toFixed(2)}</p>
                  <button 
                    onClick={() => addToCart(relatedProduct)}
                    className="neumorph-btn w-full py-2"
                    disabled={relatedProduct.stock <= 0}
                  >
                    {relatedProduct.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
